import { useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { createPartnerSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreatePartnerMutation,
  useOrganizationsQuery,
  usePartnerTemplateImportMutation,
  useProgramListQuery,
} from '@common/cache';
import { EMR_TYPE } from '@common/types';
import { omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { checkFormIsDirty } from '@util/checkFormIsDirty.ts';

interface ICreatePartnerFormShape {
  name: string;
  organization: string;
  emr: EMR_TYPE;
  emr_other?: string;
  street: string;
  suite?: string;
  city: string;
  state: string;
  zip_code: string;
  county?: string;
  google_places_id: string;
}

const initialValues: Partial<ICreatePartnerFormShape> = {
  name: '',
  organization: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  zip_code: '',
  county: '',
};

export const useCreatePartner = (
  handleOpenPartnerDetails: (id: string) => void,
) => {
  const formMethods = useForm<ICreatePartnerFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreatePartnerFormShape, any, any>(
      createPartnerSchema,
    ),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isValid, dirtyFields },
  } = formMethods;

  const isFormDirty = checkFormIsDirty(dirtyFields);

  const { mutateAsync, isPending } = useCreatePartnerMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const { mutateAsync: importMutateAsync, reset: resetUploadFile } =
    usePartnerTemplateImportMutation();

  const { data: programs = [] } = useProgramListQuery();
  const guideId = programs.find(({ name }) => name === 'GUIDE')?.id;

  const { data: organizations } = useOrganizationsQuery({
    page: 1,
    limit: 999,
    search: '',
    sort_order: 'ASC',
    sort_by: 'name',
  });

  const handleClose = () => {
    if (isFormDirty || file) {
      return setIsSaveChangesModalOpen(true);
    }
    setIsOpen(false);
    setFile(null);
    resetUploadFile();
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleChangeFile = (newFile: File | null) => {
    setFile(newFile);
    resetUploadFile();
  };

  const submitHandler = async (values: ICreatePartnerFormShape) => {
    try {
      const response: { data: { id: string } } = await mutateAsync({
        ...omit(values, 'organization'),
        record_id: values.organization,
        country: 'US',
      });

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        await importMutateAsync({
          record_id: response?.data?.id || '',
          program_id: guideId || '',
          file: formData,
        });
      }

      if (response?.data?.id) {
        handleOpenPartnerDetails(response?.data?.id);
      }

      setIsOpen(false);
      setFile(null);
      resetUploadFile();
      reset();
      toast.success('You added a new partner');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    setFile(null);
    resetUploadFile();
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      const response: { data: { id: string } } = await mutateAsync({
        ...omit(values, 'organization'),
        record_id: values.organization,
        country: 'US',
      });

      if (file) {
        const formData = new FormData();
        formData.append('file', file);

        await importMutateAsync({
          record_id: response?.data?.id || '',
          program_id: guideId || '',
          file: formData,
        });
      }
      toast.success('You added a new partner');
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      setFile(null);
      resetUploadFile();
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    formMethods,
    organizationOptions: (organizations?.records || []).map(o => ({
      value: o.id,
      label: o.name,
    })),
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleClose,
    handleOnClick,
    emrValue: useWatch({ control: formMethods.control, name: 'emr' }),
    handleSelectLocation,
    handleChangeFile,
    file,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
